import { message } from 'antd'
import { type FormikProps, withFormik } from 'formik'
import { type ComponentType, useLayoutEffect, useRef } from 'react'

interface Address {
  lines: string[]
  'lines.0'?: string
  streetAddress: string
  city: string
  zip: string
  countryCode: string
}
interface Metadata {
  title?: string
  business: boolean
}
export type Source = Address & Metadata

export interface RequiredProps {
  initialValues: Partial<Source>
  onSubmit: (source: Source) => Promise<unknown>
  translatedStrings: {
    requiredField: string
    nCharsField: (n: number) => string
    submitFailed: string
    maxCharsN: (n: number) => string
    maxNamesN: (n: number) => string
  }
  formConfig: {
    maxCharsBusinessName: number
    maxCharsPrivateName: number
    maxNames: number
  }
}
export type WithSourceFormProps<P> = P & FormikProps<Source>

export const withSourceForm = <P extends RequiredProps>({
  validationSchema,
}: {
  validationSchema: (p: P) => unknown | unknown
}) => {
  return (
    Component: ComponentType<WithSourceFormProps<P>>
  ): ComponentType<P> => {
    const LinesInvariantEnforcer = (props: WithSourceFormProps<P>) => {
      const { setFieldValue, values } = props
      const prevBusiness = useRef(values.business)
      // useLayoutEffect (not useEffect) so the corrective setFieldValue runs
      // before paint, matching the pre-render timing of the original
      // componentWillUpdate and avoiding a one-frame flash of invalid state
      useLayoutEffect(() => {
        if (values.lines.length === 0) {
          // Ensure lines[0] is always populated with a string.
          // It's simpler for input render logic to avoid the "empty" edge case
          setFieldValue('lines', [''])
        } else if (prevBusiness.current === false && values.business === true) {
          // We truncate "lines" field when switching to "business == true",
          // because a business forwarding order can only carry one name
          setFieldValue('lines', [values.lines[0]])
        }
        prevBusiness.current = values.business
      }, [values.lines, values.business, setFieldValue])
      return <Component {...props} />
    }

    return withFormik<P, Source>({
      validationSchema,
      mapPropsToValues: ({ initialValues }) => ({
        business: initialValues.business || false,
        lines:
          initialValues.lines && initialValues.lines.length > 0
            ? initialValues.lines
            : [''],
        streetAddress: initialValues.streetAddress || '',
        city: initialValues.city || '',
        zip: initialValues.zip || '',
        countryCode: 'DE',
      }),
      async handleSubmit(values, { props, resetForm, setSubmitting }) {
        try {
          await props.onSubmit(values)
          resetForm({ values })
        } catch {
          message.error(props.translatedStrings.submitFailed)
        }
        setSubmitting(false)
      },
    })(LinesInvariantEnforcer)
  }
}
